import Plot from "react-plotly.js"
import { useChartColors } from "./useChartColors"

/**
 * Reusable weekly bar chart.
 *
 * @param {string[]} xData - x-axis labels, e.g. ["Wk 2", "Wk 3", ...]
 * @param {Array<{name: string, values: number[], color?: string, width?: number}>} ySeries
 *   One or more bar series to plot. `values` must align 1:1 with `xData`.
 *   If `color` is omitted, series are colored primary/secondary/etc. in order.
 * @param {string} [title] - optional chart title, shown above the plot
 * @param {string} [xAxisLabel] - optional x-axis title
 * @param {string} [yAxisLabel] - optional y-axis title
 * @param {number} [yMax=100] - upper bound of the y-axis
 * @param {string} [yTickSuffix="%"] - suffix shown on y-axis ticks and hover
 * @param {"overlay"|"group"} [barmode="overlay"]
 * @param {boolean} [showLegend=false]
 */

export function BarChart({
    xData,
    ySeries,
    title,
    xAxisLabel,
    yAxisLabel,
    yMax = 100,
    yTickSuffix = "",
    barmode = "overlay",
    showLegend = false,
    maxXTicks = 20,
}) {
    const {secondaryColor, primaryColor} = useChartColors()
    const defaultColors = [secondaryColor, primaryColor]

    if (!xData || xData.length === 0) {
        return (
            <div className="p-5 d-flex flex-column justify-content-center align-items-center">
                <p className="text-muted">No scores available yet.</p>
            </div>
        )
    }

    const plotData = ySeries.map((s, i) => ({
        x: xData,
        y: s.values,
        type: "bar",
        name: s.name,
        marker: {color: s.color ?? defaultColors[i % defaultColors.length]},
        width: s.width,
        hovertemplate: `%{x}<br>${s.name}: %{y:.0f}${yTickSuffix}<extra></extra>`,
    }))

    const layout = {
        title: title ? {text: title, font: {size: 14}} : undefined,
        barmode,
        font: {
            family: "Inter, sans-serif"
        },
        showlegend: showLegend,
        legend: {
            orientation: "h",
            x: 0.5,
            y: 1.15,
            xanchor: "center",
            yanchor: "bottom",
        },
        autosize: true,
        height: 320,
        margin: {t: title ? 40 : showLegend ? 40 : 10, r: 10, b: 40, l: 45},
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        font: {family: "inherit", size: 12, color: "#5e6278"},
        yaxis: {
            title: yAxisLabel ? {text: yAxisLabel} : undefined,
            range: [0, yMax * 1.05 ],
            ticksuffix: yTickSuffix,
            gridcolor: "#eef1f4",
            zeroline: false,
            fixedrange: true,
        },
        xaxis: {
            title: xAxisLabel ? {text: xAxisLabel} : undefined,
            showgrid: false,
            showline: true,
            linecolor: "#eef1f4",
            fixedrange: true,
            tickmode: "array",
            tickvals: getVisibleXTicks(xData, maxXTicks),
            automargin: true,
        },
    }

    const config = {displayModeBar: false, responsive: true}

    return <Plot data={plotData} layout={layout} config={config} style={{width: "100%"}} useResizeHandler />
}

/**
 * Returns an evenly-spaced subset of xData to use as axis tick labels,
 * capped at maxTicks. If xData.length <= maxTicks, every value is shown.
 * Always includes the first and last entries when downsampling.
 *
 * @param {string[]} xData
 * @param {number} [maxTicks=20]
 * @returns {string[]}
 */
function getVisibleXTicks(xData, maxTicks = 20) {
    if (!xData || xData.length <= maxTicks) {
        return xData ?? []
    }

    const step = (xData.length - 1) / (maxTicks - 1)
    const ticks = []
    for (let i = 0; i < maxTicks; i++) {
        const index = Math.round(i * step)
        ticks.push(xData[index])
    }

    // de-duplicate in case rounding produced repeated indices on small ranges
    return [...new Set(ticks)]
}
